// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * S-expression tree types.
 *
 * This module has no KiCad knowledge whatsoever (spec §5). It models the file as
 * a generic tree so that constructs we do not understand survive a load/save
 * round trip verbatim (spec §6.1).
 */

/** A leaf token: a bare atom or a quoted string. */
export interface Atom {
  readonly kind: 'atom';
  /**
   * The *decoded* value. For quoted strings this is the text with escapes
   * resolved; the serializer re-escapes on the way out.
   *
   * Numbers are deliberately kept as strings so that a parse/serialize round
   * trip cannot lose precision or reformat (spec §6.1).
   */
  value: string;
  /** Whether the source form was double-quoted. Preserved for round-tripping. */
  quoted: boolean;
}

/** A parenthesised list. `name` is the leading bare atom, if there is one. */
export interface Node {
  readonly kind: 'node';
  /**
   * The first element of the list when it is a bare atom, else `''`.
   * KiCad always uses a leading bare atom, but the parser does not require it.
   */
  name: string;
  children: SexpChild[];
}

export type SexpChild = Node | Atom;

export function isNode(x: SexpChild | undefined): x is Node {
  return x !== undefined && x.kind === 'node';
}

export function isAtom(x: SexpChild | undefined): x is Atom {
  return x !== undefined && x.kind === 'atom';
}

export function atom(value: string, quoted?: boolean): Atom {
  return { kind: 'atom', value, quoted: quoted ?? needsQuoting(value) };
}

export function node(name: string, children: SexpChild[] = []): Node {
  return { kind: 'node', name, children };
}

/**
 * A bare atom may not contain whitespace, parentheses or quotes, and may not be
 * empty. Anything else must be emitted quoted or the file will not re-parse.
 */
export function needsQuoting(value: string): boolean {
  return value.length === 0 || /[\s()"\\;]/.test(value);
}

/** Deep copy. Used by the merge engine, which must never alias input nodes. */
export function cloneChild(x: SexpChild): SexpChild {
  if (x.kind === 'atom') return { kind: 'atom', value: x.value, quoted: x.quoted };
  return { kind: 'node', name: x.name, children: x.children.map(cloneChild) };
}

export function cloneNode(n: Node): Node {
  return cloneChild(n) as Node;
}

/**
 * Structural equality, ignoring the `quoted` flag.
 *
 * `quoted` is presentation only: KiCad writes `(width 0.2)` and `(width "0.2")`
 * interchangeably across versions, and treating those as different would produce
 * a flood of phantom diffs.
 */
export function structuralEqual(a: SexpChild, b: SexpChild): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'atom') return a.value === (b as Atom).value;
  const bn = b as Node;
  if (a.name !== bn.name) return false;
  if (a.children.length !== bn.children.length) return false;
  for (let i = 0; i < a.children.length; i++) {
    if (!structuralEqual(a.children[i]!, bn.children[i]!)) return false;
  }
  return true;
}
